/**
 * Emergency.jsx — v206.5 Health Screening Redesign
 *
 * - Header matches "My Cases" pattern: back + language pill + bell + thin divider + h1 title
 * - Page title: "Health Screening" with Required pill
 * - Orella avatar in dialogue bubble: bare logo (no light blue outline)
 * - Three large category cards, each with explicit Yes / No segmented toggle
 * - CTA gate: requires all three answered "No" before unlocking the brand-blue continue button
 * - Any "Yes" → red emergency CTA + 911 / Find ER bottom sheet
 * - R20 / PROD-163 (patient P1): every answer change is recorded with its time, and the final
 *   answers + that history go to the server IN THE CREATE-CASE CALL (casesAPI.create
 *   `urgentScreening`, the shape in utils/urgentScreening.js). orella-api (D5b) stores it once on
 *   the new case and never lets it change. This header used to say the answers "persist on the
 *   case"; they never did — they lived in the local store only, and a Yes changed to No left no
 *   trace. A screening answered for a case that ALREADY exists (the `currentCase` path) is kept
 *   locally only: the server accepts the screening at creation and nowhere else.
 * - s99 (Dr. Rami 2026-10-10, "open these one at a time, next one opens once the answer"):
 *   only one category is open. A No folds it to one line (its answer chip) and opens the next
 *   unanswered one after a 260 ms beat; a Yes keeps it open and nothing below opens. A folded
 *   answered card reopens on tap, so an answer can still change and R20 records the change;
 *   cards not reached yet are dimmed headers that cannot be opened early. Motion is the app's
 *   own fadeSlideUp curve (cubic-bezier(0.16,1,0.3,1)), off under prefers-reduced-motion.
 *   handleAnswer / handleStartCase are unchanged (check-emergency-screening slices them).
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Bell, HeartPulse, Brain, AlertOctagon, Phone, MapPin, AlertTriangle, ChevronRight, Lock, Check, Globe } from 'lucide-react'
import { useCaseStore } from '../stores'
import { casesAPI } from '../services/api'
import { useI18n } from '../i18n'
import LanguageSelector from '../components/LanguageSelector'
import { OverlayPortal } from '../components/ModalPortal'
import { Z_SECURITY } from '../utils/zLayers'
import { recordAnswerChange, buildUrgentScreening, restoreScreening } from '../utils/urgentScreening'
import { pendingEmergencyTrips, clearEmergencyTrips } from '../utils/emergencyTrips'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
import { OrellaSpinner } from '../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const LIGHT_BLUE = '#A7D9FC'
const GREEN = '#66D170'
const GREEN_DARK = '#15803D'
const RED = '#DC2626'

// ─── Typing dots ────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="flex gap-[5px] py-1">
      {[0, 0.15, 0.3].map((delay, i) => (
        <span
          key={i}
          className="w-[6px] h-[6px] rounded-full"
          style={{
            backgroundColor: LIGHT_BLUE,
            animation: `emergDotPulse 1.2s ease-in-out infinite ${delay}s`,
          }}
        />
      ))}
    </div>
  )
}

// ─── Yes / No segmented toggle ──────────────────────────────────────────────
function YesNoToggle({ value, onChange, t }) {
  const yesActive = value === 'yes'
  const noActive = value === 'no'
  return (
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        onClick={() => onChange('yes')}
        className="py-[10px] rounded-[10px] text-[13px] transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
        style={{
          border: `1.5px solid ${yesActive ? RED : '#E2E8F0'}`,
          background: yesActive ? 'rgba(220,38,38,0.08)' : '#fff',
          color: yesActive ? '#991B1B' : '#64748B',
          fontWeight: yesActive ? 700 : 500,
        }}
      >
        {yesActive && <AlertTriangle size={13} style={{ color: '#991B1B' }} />}
        {t('emergency.yes') || 'Yes'}
      </button>
      <button
        type="button"
        onClick={() => onChange('no')}
        className="py-[10px] rounded-[10px] text-[13px] transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
        style={{
          border: `1.5px solid ${noActive ? GREEN : '#E2E8F0'}`,
          background: noActive ? 'rgba(102,209,112,0.10)' : '#fff',
          color: noActive ? GREEN_DARK : '#64748B',
          fontWeight: noActive ? 700 : 500,
        }}
      >
        {noActive && <Check size={14} strokeWidth={3} style={{ color: GREEN_DARK }} />}
        {t('emergency.no') || 'No'}
      </button>
    </div>
  )
}

// ─── Single category card with Yes/No ───────────────────────────────────────
// s99: open = the one being answered; otherwise a one-line header. An answered header reopens
// on tap; a waiting one (not reached yet) is dimmed and inert.
function CategoryCard({ icon: Icon, label, items, value, onChange, open, waiting, onReopen, t }) {
  const folded = !open
  return (
    <div
      className="rounded-2xl"
      style={{
        background: waiting ? '#FAFCFD' : '#fff',
        border: `1.5px ${waiting ? 'dashed' : 'solid'} #E2E8F0`,
        boxShadow: open ? '0 10px 24px -16px rgba(21,53,79,0.35)' : 'none',
        transition: 'box-shadow 0.35s cubic-bezier(0.16,1,0.3,1), background 0.3s ease',
      }}
    >
      <button
        type="button"
        onClick={folded && !waiting ? onReopen : undefined}
        disabled={open || waiting}
        aria-expanded={open}
        className="w-full flex items-center gap-3 text-left"
        style={{ padding: folded ? '11px 13px' : '14px 14px 10px', background: 'none', border: 'none', cursor: folded && !waiting ? 'pointer' : 'default' }}
      >
        <span
          className="rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            width: folded ? 30 : 40, height: folded ? 30 : 40,
            background: waiting ? '#F1F5F8' : '#FEF2F2',
            transition: 'width 0.35s cubic-bezier(0.16,1,0.3,1), height 0.35s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <Icon size={folded ? 16 : 20} style={{ color: waiting ? '#B5C3CF' : RED }} />
        </span>
        <span className="flex-1 font-bold leading-tight" style={{ color: waiting ? '#A9B8C5' : NAVY, fontSize: folded ? 14 : 16 }}>
          {label}
        </span>
        {folded && value === 'no' && (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold rounded-full px-2.5 py-[3px]" style={{ background: 'rgba(102,209,112,0.14)', color: GREEN_DARK }}>
            <Check size={12} strokeWidth={3} />{t('emergency.no') || 'No'}
          </span>
        )}
        {folded && value === 'yes' && (
          <span className="inline-flex items-center gap-1 text-[12px] font-bold rounded-full px-2.5 py-[3px]" style={{ background: 'rgba(220,38,38,0.10)', color: '#991B1B' }}>
            <AlertTriangle size={12} />{t('emergency.yes') || 'Yes'}
          </span>
        )}
      </button>
      <div className="emerg-body" data-open={open ? 'true' : 'false'}>
        <div style={{ overflow: 'hidden' }}>
          <div className="emerg-body-in" style={{ padding: '0 14px 14px' }}>
            <ul className="m-0 mb-3 p-0 list-none flex flex-col gap-1">
              {items.map((item, i) => (
                <li key={i} className="text-[13.5px] leading-snug pl-3 relative" style={{ color: '#475569' }}>
                  <span
                    className="absolute rounded-full"
                    style={{ left: 2, top: 8, width: 4, height: 4, background: '#CBD5E1' }}
                  />
                  {item}
                </li>
              ))}
            </ul>
            <YesNoToggle value={value} onChange={onChange} t={t} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Emergency bottom sheet ──────────────────────────────────────────────────
function EmergencySheet({ onDismiss, t }) {
  const handleFindER = () => {
    window.open('https://maps.google.com/maps?q=emergency+room+near+me', '_blank')
  }

  // PROD-349: the 911 sheet sits on the security tier and is portalled to <body>.
  // Inside PageWrapper (a transformed motion.div) any z-index competes only inside
  // the page, so an overlay portalled to <body> covered "Call 911" whatever its
  // number. Stacking and anchoring only; wording and behaviour are unchanged.
  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 flex flex-col justify-end"
      style={{ zIndex: Z_SECURITY, background: 'rgba(21,53,79,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss() }}
    >
      <div
        className="flex flex-col gap-[9px]"
        style={{
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          padding: '14px 18px 36px',
          paddingBottom: 'max(36px, env(safe-area-inset-bottom, 0px))',
          animation: 'emergSlideUp 0.28s ease',
        }}
      >
        <div className="mx-auto mb-1" style={{ width: 32, height: 3, background: '#E2E8F0', borderRadius: 2 }} />
        <div
          className="mx-auto flex items-center justify-center"
          style={{ width: 44, height: 44, borderRadius: 13, background: '#FEF2F2' }}
        >
          <Phone size={22} style={{ color: RED }} />
        </div>
        <p className="text-center text-[14px] font-extrabold m-0" style={{ color: NAVY }}>
          {t('emergency.getHelpNow') || 'Please get help immediately'}
        </p>
        <p className="text-center text-[11px] m-0 leading-[1.5]" style={{ color: '#64748B' }}>
          {t('emergency.notEquipped') || "Orella is not equipped for emergencies."}
        </p>
        <a
          href="tel:911"
          className="flex items-center justify-center gap-[7px] text-[13px] font-extrabold text-white active:opacity-90 transition-opacity"
          style={{ padding: '13px', borderRadius: '100px', background: RED, textDecoration: 'none' }}
        >
          <Phone size={14} />
          {t('emergency.call911') || 'Call 911'}
        </a>
        <button
          onClick={handleFindER}
          className="flex items-center justify-center gap-[6px] text-[11.5px] font-semibold active:opacity-80 transition-opacity"
          style={{ padding: '10px', borderRadius: '10px', border: '1.5px solid #E8ECF0', color: '#64748B', background: '#fff' }}
        >
          <MapPin size={12} style={{ color: '#64748B' }} />
          {t('emergency.findNearestER') || 'Find Nearest ER'}
        </button>
        <button
          onClick={onDismiss}
          className="text-center text-[11px] font-semibold pt-1 active:opacity-70 transition-opacity"
          style={{ color: '#94A3B8', background: 'none', border: 'none' }}
        >
          ← {t('emergency.dismissEmergency') || "I'm not in an emergency"}
        </button>
      </div>
    </div>
    </OverlayPortal>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Emergency() {
  const navigate = useNavigate()
  const location = useLocation()
  const pendingCase = location.state?.pendingCase || null
  const { currentCase, updateCase, setAndSaveCase } = useCaseStore()
  const { t, currentLanguage } = useI18n()

  const [showEmergency, setShowEmergency] = useState(false)
  const [showLanguage, setShowLanguage] = useState(false)
  const [creating, setCreating] = useState(false)
  // Per-category answers: 'yes' | 'no' | null
  const [answers, setAnswers] = useState({ heart: null, stroke: null, crisis: null })
  // R20: every change, in order — { question, answer, at } (utils/urgentScreening.js)
  const [history, setHistory] = useState([])
  // s99: which card is open. By default the first not answered No; a tap on a folded answered
  // card opens it (manualOpen); after a No the card stays open for one beat (holdKey) so the
  // green No registers before the next card opens.
  const [manualOpen, setManualOpen] = useState(null)
  const [holdKey, setHoldKey] = useState(null)
  const holdTimer = useRef(null)
  useEffect(() => () => clearTimeout(holdTimer.current), [])

  // Typing animation: dots → text (once per session)
  const animPlayed = sessionStorage.getItem('orella_emergency_anim')
  const [phase, setPhase] = useState(animPlayed ? 'text' : 'dots')

  useEffect(() => {
    if (animPlayed) return
    const t1 = setTimeout(() => {
      setPhase('text')
      sessionStorage.setItem('orella_emergency_anim', 'true')
    }, 900)
    return () => clearTimeout(t1)
  }, [])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // Restore prior answers if user returns to this screen
  useEffect(() => {
    // Both the R20 shape ({ answers, history, submittedAt }) and the old local one.
    const prior = restoreScreening(currentCase?.urgentScreening || pendingCase?.urgentScreening)
    if (prior.answers.heart || prior.answers.stroke || prior.answers.crisis) {
      setAnswers(prior.answers)
      setHistory(prior.history)
    }
  }, [currentCase, pendingCase])

  // Guard — must have either an active case or a pending case from FocusAreas
  useEffect(() => {
    if (!currentCase && !pendingCase) navigate('/home', { replace: true })
  }, [currentCase, pendingCase])

  const handleBack = () => {
    const specId = currentCase?.specialtyId || currentCase?.specialty || pendingCase?.specialtyId
    if (specId) navigate(`/specialty/${specId}`, { replace: true })
    else navigate(-1)
  }

  // Counts + state derivation
  const answeredCount = ['heart', 'stroke', 'crisis'].filter((k) => answers[k]).length
  const remaining = 3 - answeredCount
  const hasYes = Object.values(answers).includes('yes')
  const allNo = answers.heart === 'no' && answers.stroke === 'no' && answers.crisis === 'no'

  // Auto-surface the emergency sheet the first time any "yes" is selected
  useEffect(() => {
    if (hasYes) setShowEmergency(true)
  }, [hasYes])

  // R20: a tap that changes an answer is recorded with its time; re-tapping the selected
  // answer records nothing.
  const handleAnswer = (key) => (val) => {
    if (answers[key] === val) return
    setHistory((prev) => recordAnswerChange(prev, answers, key, val, new Date().toISOString()))
    setAnswers((prev) => ({ ...prev, [key]: val }))
  }

  // s99: the card's own answer handler — R20's handleAnswer, then which card is open next.
  const answerCard = (key) => (val) => {
    handleAnswer(key)(val)
    clearTimeout(holdTimer.current)
    if (val === 'no') {
      setManualOpen(null)
      setHoldKey(key)
      holdTimer.current = setTimeout(() => setHoldKey(null), 260)
    } else {
      setHoldKey(null)
      setManualOpen(key)
    }
  }
  const ORDER = ['heart', 'stroke', 'crisis']
  const autoOpen = ORDER.find((k) => answers[k] !== 'no') || null
  const openKey = holdKey || manualOpen || autoOpen
  const reachIdx = autoOpen ? ORDER.indexOf(autoOpen) : ORDER.length

  const handleStartCase = async () => {
    if (!allNo) return
    setCreating(true)
    // R20: the final answers + every change, in the shape create_case validates.
    const screening = buildUrgentScreening(answers, history, new Date().toISOString())
    try {
      if (pendingCase) {
        let caseId, createdAt
        try {
          // R21 / PROD-164: the triage 911 trips that happened before this case existed go
          // with its creation; once the server holds the case AND did not say it refused or
          // dropped them (emergencyTripsStored: false), they are not carried again.
          const emergencyTrips = pendingEmergencyTrips()
          const response = await casesAPI.create(pendingCase.specialtyName, pendingCase.focusAreaName, { urgentScreening: screening, emergencyTrips })
          if (response.case?.id && response.emergencyTripsStored !== false) clearEmergencyTrips()
          caseId = response.case?.id || `local-${Date.now()}`
          createdAt = response.case?.created_at || new Date().toISOString()
        } catch (err) {
          console.error('[Emergency] API create failed, using local id:', err?.message)
          caseId = `local-${Date.now()}`
          createdAt = new Date().toISOString()
        }
        const caseNumber = caseId.startsWith('local-')
          ? `#${Math.floor(10000000 + Math.random() * 90000000)}`
          : `#${caseId.slice(-8).toUpperCase()}`
        setAndSaveCase({
          id: caseId,
          caseNumber,
          specialtyId: pendingCase.specialtyId,
          specialtyName: pendingCase.specialtyName,
          focusAreaId: pendingCase.focusAreaId,
          focusAreaName: pendingCase.focusAreaName,
          createdAt,
          status: 'draft',
          emergencyScreeningPassed: true,
          urgentScreening: screening,
          answers: {},
          voiceRecording: null,
          describeComplete: false,
          detectComplete: false,
          detect: { photos: [], documents: [], completed: false },
          diagnose: { completed: false },
          appointments: [],
        })
      } else if (currentCase) {
        updateCase({ emergencyScreeningPassed: true, urgentScreening: screening })
      }
      navigate('/case/describe/voice')
    } finally {
      setCreating(false)
    }
  }

  // Category data
  const categories = useMemo(() => [
    {
      key: 'heart',
      icon: HeartPulse,
      label: t('emergency.catHeart'),
      items: [
        t('emergency.symptom2'),
        t('emergency.symptom1'),
        t('emergency.symptomAllergy'),
      ],
    },
    {
      key: 'stroke',
      icon: Brain,
      label: t('emergency.catStroke'),
      items: [
        t('emergency.symptom3'),
        t('emergency.symptom4'),
        t('emergency.symptom6'),
        t('emergency.symptom7'),
      ],
    },
    {
      key: 'crisis',
      icon: AlertOctagon,
      label: t('emergency.catCrisis'),
      items: [
        t('emergency.symptom5'),
        t('emergency.symptomInjuryOverdose'),
        t('emergency.symptom8'),
      ],
    },
  ], [t])

  if (!currentCase && !pendingCase) return null

  return (
    <div className="min-h-full bg-white flex flex-col" style={{ position: 'relative' }}>
      <style>{`
        @keyframes emergDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes emergFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes emergSlideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .emerg-body { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.38s cubic-bezier(0.16,1,0.3,1); }
        .emerg-body[data-open="true"] { grid-template-rows: 1fr; }
        .emerg-body-in { opacity: 0; transform: translateY(10px); transition: opacity 0.25s ease, transform 0.38s cubic-bezier(0.16,1,0.3,1); }
        .emerg-body[data-open="true"] .emerg-body-in { opacity: 1; transform: none; transition-delay: 0.08s; }
        .emerg-body[data-open="false"] .emerg-body-in { visibility: hidden; transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.38s; }
        @media (prefers-reduced-motion: reduce) {
          .emerg-body, .emerg-body-in { transition: none !important; }
        }
        @keyframes breathe-blue {
          0%, 100% { box-shadow: 0 0 0 0 rgba(70,168,249,0.0), 0 3px 12px rgba(70,168,249,0.25); }
          50% { box-shadow: 0 0 0 6px rgba(70,168,249,0.12), 0 3px 16px rgba(70,168,249,0.35); }
        }
      `}</style>

      {/* ── Header — matches My Cases pattern ── */}
      <div className="px-4 pt-3 pb-0 bg-white flex-shrink-0">
        <div className="flex items-center justify-between mb-2.5">
          <button
            onClick={handleBack}
            className="p-1.5 -ml-1.5 text-gray-700 active:opacity-70 transition-opacity"
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowLanguage(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-colors"
              style={{ border: '1.5px solid #A7D9FC', backgroundColor: '#E9F8FF' }}
            >
              <Globe size={13} style={{ color: BLUE }} />
              <span className="text-[12px] font-bold" style={{ color: NAVY }}>
                {currentLanguage?.nativeName || 'English'}
              </span>
            </button>
            <button onClick={() => navigate('/notifications')} className="p-1.5 text-gray-400">
              <Bell size={20} />
            </button>
          </div>
        </div>
        <div className="h-px bg-gray-100" />
      </div>

      {/* ── Title row ── */}
      <div className="px-4 pt-3 pb-1 bg-white flex items-center justify-between flex-shrink-0">
        <h1 className="text-[22px] font-extrabold tracking-tight m-0" style={{ color: NAVY }}>
          {t('emergency.pageTitle') || 'Health Screening'}
        </h1>
        <span
          className="text-[11px] font-semibold px-2 py-[3px] rounded-md"
          style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
        >
          {t('emergency.required') || 'Required'}
        </span>
      </div>

      {/* ── Orella dialogue bubble — bare avatar, no outline ── */}
      <div className="px-4 pt-2 pb-1 flex-shrink-0">
        <div className="flex items-start gap-[9px]">
          <img
            src={orellaOIcon}
            alt="Orella"
            className="w-[34px] h-[34px] flex-shrink-0 object-contain"
            style={{ background: 'transparent', border: 'none' }}
          />
          <div
            className="flex-1 px-[13px] py-[10px]"
            style={{
              background: ICE_BLUE,
              border: `1px solid rgba(167,217,252,0.5)`,
              borderRadius: '2px 16px 16px 16px',
              animation: animPlayed ? 'none' : 'emergFadeIn 0.3s ease',
            }}
          >
            {phase === 'dots' && <TypingDots />}
            {phase === 'text' && (
              <p className="text-[13.5px] font-semibold leading-[1.4] m-0" style={{ color: NAVY }}>
                {t('emergency.orellaAsk')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-2" style={{ WebkitOverflowScrolling: 'touch' }}>
        <p
          className="text-[10px] font-bold mb-2 tracking-[0.08em] uppercase"
          style={{ color: '#94A3B8' }}
        >
          {t('emergency.callAlert') || 'Call 911 immediately if you have any of these'}
        </p>

        <div className="flex flex-col gap-2.5">
          {categories.map((cat, i) => (
            <CategoryCard
              key={cat.key}
              icon={cat.icon}
              label={cat.label}
              items={cat.items}
              value={answers[cat.key]}
              onChange={answerCard(cat.key)}
              open={openKey === cat.key}
              waiting={!answers[cat.key] && i > reachIdx}
              onReopen={() => { clearTimeout(holdTimer.current); setHoldKey(null); setManualOpen(cat.key) }}
              t={t}
            />
          ))}
        </div>

        <div
          className="flex items-start gap-[7px] mt-3 mb-3 rounded-xl"
          style={{
            background: '#FFF8F8',
            border: '1.5px solid #FECACA',
            padding: '10px 12px',
          }}
        >
          <AlertTriangle size={14} style={{ color: RED, flexShrink: 0, marginTop: 1 }} />
          <div>
            <p className="text-[12px] font-bold m-0 leading-[1.45]" style={{ color: '#991B1B' }}>
              {t('emergency.notForEmergencies') || 'Orella is not for emergencies.'}
            </p>
            <span className="text-[11.5px] font-medium block mt-[2px]" style={{ color: '#B91C1C' }}>
              {t('emergency.notForEmergenciesDetail') ||
                'If any of the above apply, stop and call 911 or go to your nearest ER now.'}
            </span>
          </div>
        </div>

        <div style={{ height: 4 }} />
      </div>

      {/* ── Sticky footer ── */}
      <div
        className="px-4 pb-[22px] pt-[10px] flex-shrink-0"
        style={{ borderTop: '1px solid #F1F5F9' }}
      >
        {hasYes ? (
          <button
            onClick={() => setShowEmergency(true)}
            className="w-full flex items-center justify-center gap-[7px] text-[13.5px] font-bold transition-all active:opacity-90"
            style={{
              padding: '13px',
              borderRadius: '100px',
              border: 'none',
              background: RED,
              color: '#fff',
            }}
          >
            <Phone size={14} />
            {t('emergency.yesEmergency') || 'I need emergency help'}
          </button>
        ) : (
          <button
            onClick={handleStartCase}
            disabled={!allNo || creating}
            className="w-full flex items-center justify-center gap-[6px] text-[13.5px] font-bold transition-all duration-300"
            style={{
              padding: '13px',
              borderRadius: '100px',
              border: 'none',
              background: allNo ? BLUE : '#F1F5F9',
              color: allNo ? '#fff' : '#CBD5E1',
              cursor: allNo && !creating ? 'pointer' : 'default',
              animation: allNo && !creating ? 'breathe-blue 2.4s ease-in-out infinite' : 'none',
            }}
          >
            {creating ? (
              <>
                <OrellaSpinner size={16} tone="inherit" />
                {t('emergency.settingUp') || 'Setting up your case...'}
              </>
            ) : !allNo ? (
              <>
                <Lock size={14} style={{ color: '#CBD5E1' }} />
                {t('emergency.answerAllToContinue') || 'Answer all three to continue'}
              </>
            ) : (
              <>
                {t('emergency.noLetsBegin') || "Let's Begin"}
                <ChevronRight size={14} style={{ color: '#fff' }} />
              </>
            )}
          </button>
        )}

        {!hasYes && !allNo && (
          <p className="text-center text-[10px] font-medium mt-[6px]" style={{ color: '#94A3B8' }}>
            {remaining === 3
              ? (t('emergency.answerEachAbove') || 'Answer each category above')
              : `${remaining} ${t('emergency.ofThreeRemaining') || 'of 3 remaining'}`}
          </p>
        )}
      </div>

      {showEmergency && (
        <EmergencySheet onDismiss={() => setShowEmergency(false)} t={t} />
      )}

      {showLanguage && (
        <LanguageSelector isOpen={showLanguage} onClose={() => setShowLanguage(false)} />
      )}
    </div>
  )
}
